/**
 * Category section with article links for help center
 */

import { Link } from 'react-router-dom';
import {
  BookOpen,
  ShoppingBag,
  User,
  CreditCard,
  Edit3,
  Wrench,
  ChevronRight,
  LucideIcon,
} from 'lucide-react';
import { HelpCategory, HelpArticleSummary } from '../../types';
import HelpArticleCard from './HelpArticleCard';

// Icon mapping for categories
const categoryIconMap: Record<string, LucideIcon> = {
  'book-open': BookOpen,
  'shopping-bag': ShoppingBag,
  'user': User,
  'credit-card': CreditCard,
  'edit-3': Edit3,
  'edit': Edit3,
  'wrench': Wrench,
};

interface HelpCategorySectionProps {
  category: HelpCategory;
  articles: HelpArticleSummary[];
  maxArticles?: number;
  showViewAll?: boolean;
}

export function HelpCategorySection({
  category,
  articles,
  maxArticles = 4,
  showViewAll = true,
}: HelpCategorySectionProps): JSX.Element {
  const Icon = categoryIconMap[category.icon] || BookOpen;
  const displayedArticles = articles.slice(0, maxArticles);
  const hasMore = articles.length > maxArticles;

  if (articles.length === 0) {
    return <></>;
  }

  return (
    <section className="mb-8">
      {/* Category Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
            <Icon size={20} className="text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {category.name}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {category.description}
            </p>
          </div>
        </div>
        {showViewAll && hasMore && (
          <Link
            to={`/help/category/${category.id}`}
            className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center gap-1 flex-shrink-0"
          >
            View all
            <ChevronRight size={14} />
          </Link>
        )}
      </div>

      {/* Articles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {displayedArticles.map(article => (
          <HelpArticleCard key={article.id} article={article} />
        ))}
      </div>
    </section>
  );
}

export default HelpCategorySection;
